import { SortingState } from '@tanstack/react-table'
import { FilterWorkersWithVisibleItems } from 'components/buttons/FilterWorkersWithVisibleItemsButtonGroup'
import {
  useLocalStorageState,
  SchemaMigrations,
} from 'hooks/use-local-storage-state'
import { createSetOperationsFor } from '@utils/createSetOperations'
import { createSortingOperationsFor } from '@utils/createSortingOperations'
import { FilterAlertType } from '@features/admin/features/submissions-admin/types'
import { Anomalies } from 'components/buttons/AnomalousTimeCardButtons'
import { useMemo } from 'react'

interface AdminLocalStorageState {
  hiddenDays: Set<number>
  hiddenStatuses: Set<Status>
  facilityIds: Set<number>
  approvalGroupIds: Set<number>
  employeeFilter: {
    name: string
    // TODO - determine if salaried/hourly should be included
    // type: ('salaried' | 'hourly' | 'unmanaged' | 'adminManaged')[]
    type: ('unmanaged' | 'adminManaged')[]
  }
  filteredAlertTypes: Set<FilterAlertType>
  filteredAnomalyTypes: Set<Anomalies>
  filterEmployeesWithVisibleTimeCards: FilterWorkersWithVisibleItems
  sorting: SortingState
}

const defaultValues: AdminLocalStorageState = {
  hiddenDays: new Set(),
  hiddenStatuses: new Set(),
  facilityIds: new Set(),
  approvalGroupIds: new Set(),
  filterEmployeesWithVisibleTimeCards: 'all',
  sorting: [
    {
      id: 'employeeName',
      desc: false,
    },
  ],
  // An empty `type` array means "all types selected" (see EmployeeFilter and
  // useFiltering). Defaulting to [] avoids having to enumerate every type here
  // and keeps new types opted-in automatically.
  employeeFilter: { name: '', type: [] },
  filteredAlertTypes: new Set(),
  filteredAnomalyTypes: new Set(),
}

const SCHEMA_VERSION = 1

export const migrations: SchemaMigrations = {
  // v1: the flat `employeeNameFilter: string` became
  // `employeeFilter: { name, type }`. Carry the user's typed name across rather
  // than dropping it on the floor.
  //
  // Version 0 is ambiguous: an un-versioned blob can be either the truly-old
  // `employeeNameFilter` shape OR the new `employeeFilter` shape written by the
  // release that shipped the rename but predates the version envelope. So this
  // must be idempotent — only synthesize `employeeFilter` from the legacy key,
  // and leave an already-present `employeeFilter` (name + type selections) alone.
  1: ({ employeeNameFilter, employeeFilter, ...rest }) => ({
    ...rest,
    employeeFilter: employeeFilter ?? {
      name: typeof employeeNameFilter === 'string' ? employeeNameFilter : '',
      type: [],
    },
  }),
}

export const useSubmissionsAdminLocalStorage = () => {
  const { state, setState, debounced, clear, isChanged } = useLocalStorageState(
    'timeLoggerSubmissionAdminState',
    defaultValues,
    { version: SCHEMA_VERSION, migrations },
  )

  return useMemo(
    () => ({
      hiddenDays: createSetOperationsFor('hiddenDays')(state, setState),
      hiddenStatuses: createSetOperationsFor('hiddenStatuses')(state, setState),
      facilityIds: {
        ...createSetOperationsFor('facilityIds')(state, setState),
        debounced: debounced?.facilityIds ?? new Set(),
      },
      approvalGroupIds: createSetOperationsFor('approvalGroupIds')(
        state,
        setState,
      ),
      filterEmployeesWithVisibleTimeCards: {
        value: state.filterEmployeesWithVisibleTimeCards,
        debounced: debounced?.filterEmployeesWithVisibleTimeCards ?? '',
        set: (value: FilterWorkersWithVisibleItems) => {
          setState((prev) => ({
            ...prev,
            filterEmployeesWithVisibleTimeCards: value,
          }))
        },
      },
      sorting: createSortingOperationsFor('sorting')(state, setState),
      employeeFilter: {
        value: state.employeeFilter,
        debounced: debounced?.employeeFilter ?? state.employeeFilter,
        set: (value: AdminLocalStorageState['employeeFilter']) => {
          setState((prev) => ({
            ...prev,
            employeeFilter: value,
          }))
        },
      },
      filteredAlertTypes: createSetOperationsFor('filteredAlertTypes')(
        state,
        setState,
      ),
      filteredAnomalyTypes: createSetOperationsFor('filteredAnomalyTypes')(
        state,
        setState,
      ),
      clear,
      isChanged,
    }),
    [state, setState, debounced, clear, isChanged],
  )
}
